import { inject } from '@angular/core';
import { BOOKING_DEBOUNCE_TIME } from '@app/shared/tokens';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BookingProductType, PostProductBookingResponse } from '@products/models/booking.model';
import { ProductBookingService } from '@products/services/product-booking.service';
import { VoucherBookingService } from '@products/services/voucher-booking.service';
import { VoucherValidationService } from '@products/services/voucher-validation.service';
import { VoucherService } from '@products/services/voucher.service';
import { VoucherProductBookingActions } from '@store/products/booking/product/voucher/voucher-booking.actions';
import { getInitialVoucherTariffByTicketPersonId } from '@store/products/product-selection/product-selection.selectors';
import { VoucherProductActions } from '@store/products/product.actions';
import { isEmpty, isNil } from 'lodash';
import { catchError, concatMap, debounceTime, EMPTY, map, of, switchMap, withLatestFrom } from 'rxjs';

export const setRedeemedAndReleaseVouchersInProductBookingsAndProductSelection$ = createEffect(
  (
    actions$ = inject(Actions),
    voucherBookingService = inject(VoucherBookingService),
    voucherService = inject(VoucherService),
    voucherValidationService = inject(VoucherValidationService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(VoucherProductActions.setRedeemedAndReleaseVouchersInProductBookingsAndProductSelection),
      switchMap(({ redeemedVoucher, releaseVouchers, removeProductBookings }) => {
        if (isEmpty(redeemedVoucher)) {
          return EMPTY;
        }

        const { voucherType, ticketPersonId, ticketCount, isAnonymous } = redeemedVoucher;

        return voucherService.releaseMultipleVouchers$(releaseVouchers).pipe(
          switchMap(() => {
            return voucherValidationService
              .validateRedeemedVoucherTypeCount$(voucherType, ticketCount, isAnonymous)
              .pipe(
                withLatestFrom(store.select(getInitialVoucherTariffByTicketPersonId(ticketPersonId))),
                map(([validatedVoucherCount, voucherTariff]) => {
                  const redeemedVoucherProductBooking: BookingProductType =
                    voucherBookingService.mapRedeemedVoucherToBookingTariffType(
                      redeemedVoucher,
                      voucherTariff,
                      validatedVoucherCount
                    );
                  const updatedProductBookings: BookingProductType[] = [
                    redeemedVoucherProductBooking,
                    ...removeProductBookings
                  ];

                  if (isAnonymous) {
                    return VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList(
                      updatedProductBookings
                    );
                  }

                  return VoucherProductBookingActions.setVoucherProductInBookingList(updatedProductBookings);
                })
              );
          }),
          catchError(() => {
            if (isAnonymous) {
              return of(VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList([]));
            }

            return of(VoucherProductBookingActions.setVoucherProductInBookingList([]));
          })
        );
      })
    ),
  { functional: true }
);

export const removeReleasedVoucherTariffBooking$ = createEffect(
  (actions$ = inject(Actions), voucherBookingService = inject(VoucherBookingService)) =>
    actions$.pipe(
      ofType(VoucherProductActions.removeReleasedVoucher),
      concatMap(({ ticketPersonId, voucherCode }) => {
        if (isNil(ticketPersonId) || isEmpty(voucherCode)) {
          return EMPTY;
        }

        return voucherBookingService.removeReleasedVoucherTariffBookings$({ ticketPersonId, voucherCode }).pipe(
          map(({ bookedVoucherTariffTypes, voucherCode }) => {
            if (bookedVoucherTariffTypes.length) {
              return VoucherProductBookingActions.removeReleasedVoucherProductFromBookingList(bookedVoucherTariffTypes);
            }

            return VoucherProductActions.removeReleasedAndRemovedProductBookingVoucherFromProductSelection(voucherCode);
          })
        );
      })
    ),
  { functional: true }
);

export const removeVoucherTariffBooking$ = createEffect(
  (
    actions$ = inject(Actions),
    bookingDebounceTime = inject(BOOKING_DEBOUNCE_TIME, { optional: true }),
    productBookingService = inject(ProductBookingService),
    voucherBookingService = inject(VoucherBookingService)
  ) =>
    actions$.pipe(
      ofType(VoucherProductActions.removeVoucherTariffBooking),
      debounceTime(bookingDebounceTime),
      switchMap(({ ticketPersonId, voucherCode }) => {
        if (isNil(ticketPersonId) || isEmpty(voucherCode)) {
          return EMPTY;
        }

        return voucherBookingService.removeVoucherTariffBookings$({ ticketPersonId, voucherCode }).pipe(
          switchMap(({ bookingTariffTypes, removePostProductBookings }) => {
            return productBookingService.postProductBookings$(removePostProductBookings).pipe(
              map((postProductBookingsResponse: PostProductBookingResponse[]) => {
                const products: BookingProductType[] =
                  productBookingService.mapPostProductBookingsResponseToBookingProductTypes(
                    bookingTariffTypes,
                    postProductBookingsResponse
                  );

                return VoucherProductBookingActions.removeVoucherProductFromBookingList(products);
              })
            );
          }),
          catchError(() => of(VoucherProductBookingActions.removeVoucherProductFromBookingList([])))
        );
      })
    ),
  { functional: true }
);
